import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Space,
  Divider,
  Empty,
  Spin,
  Alert,
  InputNumber,
  Popconfirm,
  message,
  Input,
  Tag,
} from 'antd';
import {
  ShoppingCartOutlined,
  DeleteOutlined,
  ShoppingOutlined,
  EnvironmentOutlined,
  TagOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import cartService from '../../services/cartService';
import './CartPage.scss';

const { Title, Text } = Typography;

const CartPage = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartStore } = useStores();
  const [couponInputs, setCouponInputs] = useState({});
  const [applyingCoupon, setApplyingCoupon] = useState({});

  useEffect(() => {
    cartStore.fetchCart();
  }, [cartStore]);

  const handleCouponInputChange = (itemId, value) => {
    setCouponInputs(prev => ({ ...prev, [itemId]: value.toUpperCase() }));
  };

  const handleApplyItemCoupon = async (itemId) => {
    const couponCode = couponInputs[itemId]?.trim();
    if (!couponCode) {
      message.warning(t('cart.coupon.enterCode'));
      return;
    }

    setApplyingCoupon(prev => ({ ...prev, [itemId]: true }));
    const result = await cartService.applyItemCoupon(itemId, couponCode);
    setApplyingCoupon(prev => ({ ...prev, [itemId]: false }));

    if (result.success) {
      message.success(t('cart.coupon.applied'));
      setCouponInputs(prev => ({ ...prev, [itemId]: '' }));
      cartStore.fetchCart(); // Refresh cart to show updated totals
    } else {
      message.error(result.error);
    }
  };

  const handleRemoveItemCoupon = async (itemId) => {
    setApplyingCoupon(prev => ({ ...prev, [itemId]: true }));
    const result = await cartService.removeItemCoupon(itemId);
    setApplyingCoupon(prev => ({ ...prev, [itemId]: false }));

    if (result.success) {
      message.info(t('cart.coupon.removed'));
      cartStore.fetchCart(); // Refresh cart to show updated totals
    } else {
      message.error(result.error);
    }
  };

  const handleUpdateQuantity = async (cartItemId, newQuantity) => {
    const result = await cartStore.updateCartItem(cartItemId, newQuantity);
    if (!result.success) {
      message.error(result.error || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (cartItemId) => {
    const result = await cartStore.removeCartItem(cartItemId);
    if (result.success) {
      message.success('Item removed from cart');
    } else {
      message.error(result.error || 'Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    const result = await cartStore.clearCart();
    if (result.success) {
      message.success('Cart cleared');
    } else {
      message.error(result.error || 'Failed to clear cart');
    }
  };

  const handleUnifiedCheckout = () => {
    // Navigate to unified checkout page
    navigate('/checkout', {
      state: {
        unified: true,
        partners: cartStore.cart.map(g => ({
          partnerId: g.partner_id,
          partnerName: g.partner_name,
          isImpactPartner: g.is_impact_partner,
          impactPercentage: g.impact_percentage,
          subtotal: g.subtotal,
          shippingCost: g.shipping_cost,
          total: g.total,
        })),
        totalAmount: cartStore.cartTotal,
      }
    });
  };

  if (cartStore.loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('cart.loading')} />
      </div>
    );
  }

  if (cartStore.error) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('common.error')}
          description={cartStore.error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (cartStore.isEmpty) {
    return (
      <div className="cart-page">
        <div className="sweet-container">
          <Card className="empty-cart-card">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size="large">
                  <Title level={3}>{t('cart.empty.title')}</Title>
                  <Text type="secondary">
                    {t('cart.empty.description')}
                  </Text>
                </Space>
              }
            >
              <Button
                type="primary"
                size="large"
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/products')}
              >
                {t('cart.empty.browseProducts')}
              </Button>
            </Empty>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="sweet-container">
        <div className="cart-header">
          <Title level={2}>
            <ShoppingCartOutlined /> {t('cart.title')}
          </Title>
          <Space>
            <Text type="secondary">{cartStore.totalItems} {t('cart.items')}</Text>
            <Popconfirm
              title={t('cart.clearConfirmTitle')}
              description={t('cart.clearConfirmDesc')}
              onConfirm={handleClearCart}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button danger>{t('cart.clearCart')}</Button>
            </Popconfirm>
          </Space>
        </div>

        <Divider />

        {/* Cart items grouped by partner */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {cartStore.cart.map((partnerGroup) => (
                <Card
                  key={partnerGroup.partner_id}
                  className="partner-group-card"
                  title={
                    <Space>
                      <EnvironmentOutlined />
                      <Text strong>{partnerGroup.partner_name}</Text>
                    </Space>
                  }
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {partnerGroup.items.map((item) => (
                      <Card key={item.id} className="cart-item-card" type="inner">
                        <Row gutter={16} align="middle">
                          {/* Product Image */}
                          <Col xs={24} sm={6}>
                            <div className="cart-item-image">
                              {item.product_image ? (
                                <img src={item.product_image} alt={item.product_name} />
                              ) : (
                                <div className="image-placeholder">
                                  <ShoppingCartOutlined />
                                </div>
                              )}
                            </div>
                          </Col>

                          {/* Product Details */}
                          <Col xs={24} sm={10}>
                            <Space direction="vertical" size="small">
                              <Text strong className="product-name">
                                {item.product_name}
                              </Text>
                              <Text type="secondary">
                                {item.variant_size && `Size: ${item.variant_size}`}
                                {item.variant_color && ` • Color: ${item.variant_color}`}
                                {item.variant_weight && ` • Weight: ${item.variant_weight}kg`}
                              </Text>
                              <Text strong style={{ color: '#FF9A76' }}>
                                €{parseFloat(item.unit_price).toFixed(2)} each
                              </Text>
                            </Space>
                          </Col>

                          {/* Quantity and Actions */}
                          <Col xs={24} sm={8}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <div className="quantity-control">
                                <Text type="secondary">{t('common.quantity')}:</Text>
                                <InputNumber
                                  min={1}
                                  max={item.stock_available}
                                  value={item.quantity}
                                  onChange={(value) => handleUpdateQuantity(item.id, value)}
                                  style={{ width: '100%' }}
                                />
                              </div>

                              {/* Per-item coupon */}
                              {item.coupon_code ? (
                                <div className="applied-coupon">
                                  <Tag color="green" icon={<CheckCircleOutlined />}>
                                    {item.coupon_code}
                                    {item.coupon_discount_type === 'PERCENTAGE'
                                      ? ` (-${item.coupon_discount_value}%)`
                                      : ` (-€${item.coupon_discount_value})`}
                                  </Tag>
                                  <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    loading={applyingCoupon[item.id]}
                                    onClick={() => handleRemoveItemCoupon(item.id)}
                                  />
                                </div>
                              ) : (
                                <div className="coupon-input">
                                  <Input.Group compact style={{ display: 'flex' }}>
                                    <Input
                                      placeholder={t('cart.coupon.placeholder')}
                                      value={couponInputs[item.id] || ''}
                                      onChange={(e) => handleCouponInputChange(item.id, e.target.value)}
                                      onPressEnter={() => handleApplyItemCoupon(item.id)}
                                      style={{ flex: 1 }}
                                      size="small"
                                    />
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<TagOutlined />}
                                      loading={applyingCoupon[item.id]}
                                      onClick={() => handleApplyItemCoupon(item.id)}
                                    />
                                  </Input.Group>
                                </div>
                              )}

                              {/* Show discount if applied */}
                              {item.discount_amount && parseFloat(item.discount_amount) > 0 ? (
                                <>
                                  <Text delete type="secondary" style={{ fontSize: 12 }}>
                                    €{parseFloat(item.subtotal).toFixed(2)}
                                  </Text>
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {t('common.subtotal')}: €{parseFloat(item.final_subtotal).toFixed(2)}
                                  </Text>
                                </>
                              ) : (
                                <Text strong>
                                  {t('common.subtotal')}: €{parseFloat(item.subtotal).toFixed(2)}
                                </Text>
                              )}

                              <Popconfirm
                                title="Remove item?"
                                onConfirm={() => handleRemoveItem(item.id)}
                                okText={t('common.yes')}
                                cancelText={t('common.no')}
                              >
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                >
                                  {t('common.remove')}
                                </Button>
                              </Popconfirm>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    {/* Partner Group Summary */}
                    <Card className="partner-summary" type="inner">
                      <Row justify="space-between">
                        <Col>
                          <Space direction="vertical" size="small">
                            <Text type="secondary">{t('cart.itemsSubtotal')}</Text>
                            <Text type="secondary">{t('cart.shippingCost')}</Text>
                            <Title level={4} style={{ margin: 0 }}>{t('common.total')}:</Title>
                          </Space>
                        </Col>
                        <Col>
                          <Space direction="vertical" size="small" align="end">
                            <Text>€{parseFloat(partnerGroup.subtotal).toFixed(2)}</Text>
                            <Text>€{parseFloat(partnerGroup.shipping_cost).toFixed(2)}</Text>
                            <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                              €{parseFloat(partnerGroup.total).toFixed(2)}
                            </Title>
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  </Space>
                </Card>
              ))}
            </Space>
          </Col>

          {/* Cart Summary Sidebar */}
          <Col xs={24} lg={8}>
            <Card className="cart-summary-card" title={t('cart.orderSummary')}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div className="summary-row">
                  <Text type="secondary">{t('cart.totalItems')}</Text>
                  <Text strong>{cartStore.totalItems}</Text>
                </div>
                <div className="summary-row">
                  <Text type="secondary">{t('cart.cartTotal')}</Text>
                  <Title level={3} style={{ margin: 0, color: '#FF9A76' }}>
                    €{cartStore.cartTotal.toFixed(2)}
                  </Title>
                </div>
                <Divider />
                {/* Unified checkout button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingOutlined />}
                  onClick={handleUnifiedCheckout}
                  block
                  style={{ marginBottom: 12 }}
                >
                  {t('cart.checkoutAll')}
                </Button>
                {/* Show info when there are multiple partners */}
                {new Set(cartStore.cart.map(g => g.partner_id)).size > 1 && (
                  <Alert
                    message={t('cart.multipleSellers.title')}
                    description={t('cart.multipleSellers.unifiedDescription')}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Button
                  type="default"
                  size="large"
                  onClick={() => navigate('/products')}
                  block
                >
                  {t('cart.continueShopping')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
});

export default CartPage;
